import {
  Box,
  CollapsibleCard,
  Grid,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import {
  getDormantPoolGridAction,
  markCustomerNotInterestedApi,
  takeCustomerApi,
} from '@store/dormantPool';
import { dormatPoolStore, setDormantPoolStore } from '@store/dormantPool/store';
import { EyeIcon } from '@atgfr8/icons';
import { DormantPoolListType } from '@typeDefinitions/dormantPoolTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatDateString } from '@utils/dateFormat';
import { handleToast } from '@utils/utils';
import { Accessor, createSignal, onMount, Setter } from 'solid-js';

import { ClaimedCustomers } from './ClaimedCustomers';

type GridViewProps = {
  hideNotInterested: Accessor<boolean>;
  viewMode: Accessor<'List' | 'Grid'>;
  isGridLoading: Accessor<boolean>;
  setIsGridLoading: Setter<boolean>;
  onCustomerRemovedFromGrid?: () => void;
};

const GridCardSkeleton = () => (
  <CollapsibleCard
    maxContentHeight={230}
    endTitle={
      <Box class="flex gap-2 justify-end px-4 pb-2">
        {[...Array(2)].map(() => (
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            class="!bg-[#045E86]"
          />
        ))}
      </Box>
    }
    class="mb-2"
  >
    <Stack spacing={1.5} padding={2}>
      {[28, 20, 20, 20, 20].map((height) => (
        <Skeleton
          variant="text"
          width="80%"
          height={height}
          class="!bg-[#045E86]"
        />
      ))}
    </Stack>
  </CollapsibleCard>
);

export const GridView = (props: GridViewProps) => {
  onMount(async () => {
    props.setIsGridLoading(true);
    const payload = {
      hideNotInterested: props.hideNotInterested(),
    };
    await getDormantPoolGridAction(payload);
    props.setIsGridLoading(false);
  });

  const [takeCustomerLoadingMap, setTakeCustomerLoadingMap] = createSignal<
    Record<string, boolean>
  >({});
  const [toggleInterestLoadingMap, setToggleInterestLoadingMap] = createSignal<
    Record<string, boolean>
  >({});

  const toggleCustomerInterest = async (data: DormantPoolListType) => {
    setToggleInterestLoadingMap((prev) => ({ ...prev, [data.id]: true }));

    const status = data.isNotInterested;
    const response = await markCustomerNotInterestedApi(data.id, status);

    if (Boolean(response)) {
      const currentList = dormatPoolStore.gridView.data;
      const index = currentList.findIndex((item) => item.id === data.id);
      if (index !== -1) {
        const updatedStatus = !status;
        const updatedCustomer = { ...data, isNotInterested: updatedStatus };
        const updatedList = [...currentList];

        if (props.hideNotInterested() && updatedStatus) {
          updatedList.splice(index, 1);
        } else {
          updatedList[index] = updatedCustomer;
        }

        setDormantPoolStore('gridView', 'data', updatedList);
        handleToast(
          ToastType.Success,
          `Customer marked as ${
            updatedStatus ? 'Not Interested' : 'Interested'
          }.`,
        );
      }
    }

    setToggleInterestLoadingMap((prev) => ({ ...prev, [data.id]: false }));
  };

  const handleTake = async (customer: DormantPoolListType) => {
    setTakeCustomerLoadingMap((prev) => ({ ...prev, [customer.id]: true }));

    const response = await takeCustomerApi(customer.id);

    if (Boolean(response)) {
      const claimedCustomer = { ...customer, dateClaimed: new Date() };
      const updatedClaimedList = [...dormatPoolStore.claimed, claimedCustomer];
      setDormantPoolStore('claimed', updatedClaimedList);

      const currentListView = dormatPoolStore.gridView.data;
      const customerIndex = currentListView.findIndex(
        (c) => c.id === customer.id,
      );

      if (customerIndex !== -1) {
        const updatedListView = [...currentListView];
        updatedListView.splice(customerIndex, 1);
        setDormantPoolStore('gridView', 'data', updatedListView);
      }

      handleToast(ToastType.Success, 'Customer claimed.');
    }

    setTakeCustomerLoadingMap((prev) => ({ ...prev, [customer.id]: false }));
  };

  const DetailLine = (props: {
    label: string;
    value: string | number | null;
  }) => (
    <Typography variant="h5" class="text-[16px]!">
      <Box class="inline-block" fontWeight="bold" mr={0.5}>
        {props.label}:
      </Box>
      {props.value}
    </Typography>
  );

  return (
    <Box class="min-h-0 flex-1 overflow-auto">
      <Grid container spacing={4}>
        <Grid item size={{ xs: 12, lg: 7 }}>
          <Grid container spacing={4}>
            {props.isGridLoading() ? (
              <>
                {[...Array(4)].map(() => (
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <GridCardSkeleton />
                  </Grid>
                ))}
                <Grid item size={12} class="pb-8">
                  <></>
                </Grid>
              </>
            ) : dormatPoolStore.gridView.data.length === 0 ? (
              <Grid item size={12}>
                <Box class="border w-[100%]! border-gray-300 px-4 py-3 bg-[#f5f5f5] text-base text-center min-h-[56px] !block text-[20px]!">
                  No Customers Found Matching your Criteria.
                </Box>
              </Grid>
            ) : (
              dormatPoolStore.gridView.data.map((item) => (
                <Grid item size={{ xs: 12, md: 6 }}>
                  <CollapsibleCard
                    endTitle={
                      <Box class="flex gap-2 justify-end items-center">
                        <Button
                          label={item.dateClaimed ? 'Claimed' : 'Take'}
                          color="success"
                          size="sm"
                          onClick={async () => {
                            // eslint-disable-next-line no-alert
                            const confirm = window.confirm(
                              `Are you sure you want to take customer ${item.customerName} ?`,
                            );
                            if (confirm) {
                              await handleTake(item);
                            }
                          }}
                          disabled={
                            !item.id ||
                            !!takeCustomerLoadingMap()[item.id] ||
                            item.dateClaimed != null ||
                            dormatPoolStore.claimed.length >= 5
                          }
                          isLoading={
                            !!item.id && !!takeCustomerLoadingMap()[item.id]
                          }
                          dataTestId={formatDataTestId(
                            'Claimed Take',
                            'button',
                          )}
                        />
                        <Button
                          label={
                            item.isNotInterested
                              ? 'Interested'
                              : 'Not Interested'
                          }
                          color={item.isNotInterested ? 'primary' : 'error'}
                          size="sm"
                          onClick={() => toggleCustomerInterest(item)}
                          disabled={
                            !item.id || !!toggleInterestLoadingMap()[item.id]
                          }
                          isLoading={
                            !!item.id && !!toggleInterestLoadingMap()[item.id]
                          }
                          dataTestId={formatDataTestId(
                            'Interested  Not Interested',
                            'button',
                          )}
                        />
                        {item.seenBefore && (
                          <Tooltip
                            title="You have seen this customer before"
                            class="z-50"
                            placement="bottom"
                            dataTestId="dormant-pool-seen-before-tooltip"
                          >
                            <EyeIcon />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    maxContentHeight={230}
                    class="mb-2 overflow-visible"
                  >
                    <Stack spacing={2}>
                      <Typography
                        variant="h4"
                        class="text-[20px]!"
                        sxProps={{ fontWeight: 'bold' }}
                      >
                        {item.customerName}
                      </Typography>

                      <DetailLine label="Industry" value={item.industry} />
                      <DetailLine
                        label="Last Load Date"
                        value={formatDateString(item.lastDropoffDate)}
                      />
                      <DetailLine label="Loads Moved" value={item.loads} />
                      <DetailLine label="Equipment" value={item.equipments} />
                      <DetailLine
                        label="Pickup State(s)"
                        value={item.originStates}
                      />
                      <DetailLine
                        label="Dropoff State(s)"
                        value={item.destinationStates}
                      />
                    </Stack>
                  </CollapsibleCard>
                </Grid>
              ))
            )}
          </Grid>
        </Grid>

        <Grid item size={{ xs: 12, lg: 4 }}>
          <ClaimedCustomers
            viewMode={props.viewMode}
            onCustomerRemovedFromGrid={props.onCustomerRemovedFromGrid}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
